import { encodePeriodValue, formatPeriodTriggerLabel, fromLumenPeriodValue, getMonthNames } from '@components'

import { Modal, type ModalProps, notify, useModal, withModalConditionalRender } from '@billy/design-system'
import { trackError } from '@billy/track-error'
import {
  Button,
  type PeriodValue as LumenPeriodValue,
  PeriodPicker,
  Select,
  type SelectOption,
  XStack,
  YStack,
} from '@shinetools/lumen-react'
import { useCallback, useId, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import type { Contact } from '@features/contacts/types/contact'

import { DesignSystem } from '../../../../../../../../../../../../enums/designSystem'
import { NotificationKeys } from '../../../../../../../../../../../../enums/notificationKeys'
import { PeriodMode } from '../../../../../../../../../../../../enums/periodMode'
import { useForm } from '../../../../../../../../../../../../hooks/useForm'
import { useLocale } from '../../../../../../../../../../../../hooks/useLocale'
import { useOrganizationFiscalYears } from '../../../../../../../../../../../../hooks/useOrganizationFiscalYears'
import { useExportContactAccountStatement } from './hooks/useExportContactAccountStatement'
import type { ContactAccountStatementFormat, FetchContactAccountStatementParams } from './query-api'
import {
  ACCOUNT_STATEMENT_FORMATS,
  DEFAULT_PERIOD,
  getSupplierStatementExportFormDefaultValues,
  getSupplierStatementExportFormSchema,
  type SupplierStatementExportFormValues,
} from './utils/supplierStatementExportForm'

const AVAILABLE_PERIOD_MODES = ['month', 'quarter', 'fiscalYear', 'custom'] as const
const ACCOUNT_STATEMENT_FORMAT_LABEL_KEY: Record<ContactAccountStatementFormat, string> = {
  csv: 'suppliers.detail.statement.format.csv',
  html: 'suppliers.detail.statement.format.html',
  pdf: 'suppliers.detail.statement.format.pdf',
  xlsx: 'suppliers.detail.statement.format.xlsx',
}

interface SupplierStatementExportModalProps extends ModalProps {
  contact: Pick<Contact, 'id' | 'name' | 'organizationId'>
}

const SupplierStatementExportModalComponent = ({ contact, ...modalProps }: SupplierStatementExportModalProps) => {
  const { t } = useTranslation()
  const locale = useLocale()
  const formId = useId()
  const { maxDate, minDate } = useOrganizationFiscalYears()
  const { close: closeSupplierStatementExportModal } = useModal(modalProps.id)

  const formatOptions = useMemo<SelectOption[]>(
    () =>
      ACCOUNT_STATEMENT_FORMATS.map((value) => ({
        label: t(ACCOUNT_STATEMENT_FORMAT_LABEL_KEY[value]),
        value,
      })),
    [t],
  )
  const monthLabels = useMemo(() => getMonthNames(locale.code), [locale.code])

  const handleFormatPeriodLabel = useCallback(
    (value: LumenPeriodValue) => formatPeriodTriggerLabel({ locale, monthLabels, t, value }),
    [locale, monthLabels, t],
  )

  const { Form, FormItem } = useForm({
    defaultValues: useMemo(() => getSupplierStatementExportFormDefaultValues(), []),
    designSystem: DesignSystem.Lumen,
    onSubmit: ({ format, period }: SupplierStatementExportFormValues) => {
      const periodValue = fromLumenPeriodValue(period)

      exportStatement({
        contactId: contact.id,
        format,
        organizationId: contact.organizationId,
        period:
          periodValue && periodValue.mode !== PeriodMode.All
            ? encodePeriodValue(periodValue, contact.organizationId)
            : undefined,
      })
    },
    validationSchema: useMemo(() => getSupplierStatementExportFormSchema(), []),
  })

  const handleExportSuccess = (statement: Blob, { format }: FetchContactAccountStatementParams) => {
    const downloadUrl = URL.createObjectURL(statement)
    const downloadLink = document.createElement('a')

    downloadLink.href = downloadUrl
    downloadLink.download = `${contact.name}-account-statement.${format}`
    document.body.appendChild(downloadLink)
    downloadLink.click()
    downloadLink.remove()
    window.setTimeout(() => URL.revokeObjectURL(downloadUrl), 200)
    closeSupplierStatementExportModal()
  }

  const handleExportError = (error: unknown) => {
    trackError(error)
    notify({
      id: NotificationKeys.ContactStatementExport,
      message: t('suppliers.detail.statement.notification.error'),
      variant: 'error',
    })
  }

  const { exportStatement, isExporting } = useExportContactAccountStatement({
    onError: handleExportError,
    onSuccess: handleExportSuccess,
  })

  return (
    <Modal {...modalProps} closable={!isExporting} size="s">
      <Modal.Header title={t('suppliers.detail.statement.title', { name: contact.name })} />
      <Modal.Body>
        <Form id={formId}>
          <YStack gap={16}>
            <FormItem
              name="period"
              render={({ field }) => (
                <PeriodPicker
                  allTimeLabel={t('period_select.mode.all')}
                  availableModes={AVAILABLE_PERIOD_MODES}
                  disabled={isExporting}
                  formatTriggerLabel={handleFormatPeriodLabel}
                  label={t('exports.period')}
                  labels={{
                    customMode: t('expert_reports.period_select.mode.custom'),
                    customRangeTitle: t('period_select.mode.custom_date'),
                    endDatePlaceholder: t('period_select.custom_date.date_placeholder'),
                    fiscalYearMode: t('expert_reports.period_select.mode.fiscal_year'),
                    monthMode: t('period_select.mode.month'),
                    quarterMode: t('period_select.mode.quarter'),
                    startDatePlaceholder: t('period_select.custom_date.date_placeholder'),
                  }}
                  maxDate={maxDate}
                  minDate={minDate}
                  monthLabels={monthLabels}
                  onChange={(value: LumenPeriodValue | null) => field.onChange(value ?? DEFAULT_PERIOD)}
                  placeholder={t('period_select.trigger_button_placeholder')}
                  saveLabel={t('period_select.save_button')}
                  value={field.value}
                />
              )}
            />
            <FormItem
              name="format"
              render={({ error, field }) => (
                <Select
                  disabled={isExporting}
                  error={error}
                  label={t('exports.as')}
                  onChange={field.onChange}
                  options={formatOptions}
                  value={field.value}
                />
              )}
            />
          </YStack>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <XStack gap={8} justify="flex-end">
          <Button
            disabled={isExporting}
            label={t('close')}
            onClick={closeSupplierStatementExportModal}
            variant="text"
          />
          <Button
            color="primary"
            form={formId}
            label={t('export')}
            loading={isExporting}
            type="submit"
            variant="filled"
          />
        </XStack>
      </Modal.Footer>
    </Modal>
  )
}

export const SupplierStatementExportModal = withModalConditionalRender(SupplierStatementExportModalComponent)
